import type { SipConfig, PersistedSipConfig } from './index.types';
import localforage from 'localforage';
import { PERSISTED_SIP_CONFIG, SIP_CONFIG_MAX_AGE_MS } from './index.constants';

export type { SipConfig, PersistedSipConfig } from './index.types';

/**
 * Get persisted SIP configuration from localforage.
 * Returns null if config doesn't exist, is malformed, or is too old.
 *
 * @returns The stored SIP config, or null if not available/valid.
 */
export const getPersistedSipConfig = async (): Promise<SipConfig | null> => {
  try {
    const stored = await localforage.getItem<PersistedSipConfig>(
      PERSISTED_SIP_CONFIG
    );

    if (!stored) {
      return null;
    }

    // Validate structure
    if (!stored.config || !stored.timestamp) {
      return null;
    }

    // Check if config is too old
    const age = Date.now() - stored.timestamp;
    if (age > SIP_CONFIG_MAX_AGE_MS) {
      // Clean up stale config
      await localforage.removeItem(PERSISTED_SIP_CONFIG);
      return null;
    }

    // Validate required fields
    const { config } = stored;
    if (
      !config.uri ||
      !config.authorizationUsername ||
      !config.authorizationPassword ||
      !config.wsServers ||
      !config.domain
    ) {
      return null;
    }

    return config;
  } catch {
    // If there's any error reading, return null
    return null;
  }
};

/**
 * Persist SIP configuration to localforage.
 * This is called after successful SIP initialization to provide
 * a fallback for reconnection scenarios.
 *
 * @param config - The SIP config to persist.
 */
export const setPersistedSipConfig = async (
  config: SipConfig
): Promise<void> => {
  try {
    const stored: PersistedSipConfig = {
      config,
      timestamp: Date.now(),
    };
    await localforage.setItem(PERSISTED_SIP_CONFIG, stored);
  } catch {
    // Silently fail - this is a best-effort fallback mechanism
  }
};

/**
 * Clear persisted SIP configuration from localforage.
 * Should be called on logout or when config is known to be invalid.
 */
export const clearPersistedSipConfig = async (): Promise<void> => {
  try {
    await localforage.removeItem(PERSISTED_SIP_CONFIG);
  } catch {
    // Silently fail
  }
};
